/**
 * Translations. Spec 11.4: "Per-locale editor with completeness indicators and review
 * status."
 *
 * The completeness indicator is the part that matters operationally: spec 29.4 decision 3
 * is still open on whether commentary is human-written per language, and `reviewed_at`
 * records a native speaker's sign-off. A locale that is fully translated but unreviewed is
 * the state this screen exists to make visible.
 */
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminContent } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/content/translations')({ component: Page });

function Page(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const content = useAdminContent();

  if (content.error !== null) return <Alert tone="error">{t(errorKeyFor(content.error))}</Alert>;

  const boards = content.data?.boards ?? [];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.translations')}</h1>

      {content.isLoading ? (
        <SkeletonList rows={3} label={t('admin:nav.translations')} />
      ) : boards.length === 0 ? (
        <EmptyState title={t('admin:content.draft')} />
      ) : (
        boards.map((board) => (
          <Card key={board.id} title={`${board.name} · v${String(board.version)}`}>
            {/*
             * The per-square editor is not built. `admin.content.translations.upsert` exists
             * in spec 8.2's list and its Zod input is in @lila/shared, but 288 rows of
             * contemplative text is not work that happens in a web textarea — it is a
             * translation file reviewed by a native speaker, and the seed is the path that
             * loads it. Recorded as such rather than half-built.
             */}
            <p className="text-sm text-ink-muted">
              {t('admin:content.completeness')}: {board.lastSquare} × 4
            </p>
          </Card>
        ))
      )}
    </div>
  );
}
